import { type Layer, ObjectCategory } from "@common/constants";
import { RoofState } from "@common/definitions/storm";
import { type Hitbox, HitboxType } from "@common/utils/hitbox";
import { adjacentOrEquivLayer } from "@common/utils/layer";
import { Container, GlProgram, Graphics, Mesh, MeshGeometry, RendererType, RenderTexture, Shader, type Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { CameraManager } from "./cameraManager";
import { Lighting } from "./lighting";
import { blocksLight, type Cone, fanAmong, fanCapacity, fanIndices, LIGHT_HEIGHT, MOST_FANS, nearSegments, rayReach, RELIEF_FRAGMENT, RELIEF_SCALE, RELIEF_VERTEX, segmentsOf, STILL, type UvMap } from "./lightShadowLogic";

export { beamUv, LIGHT_HEIGHT, MOST_FANS, RELIEF_FRAGMENT, RELIEF_SCALE, RELIEF_VERTEX, roundUv, type UvMap } from "./lightShadowLogic";
import { NormalLook } from "./normalLook";

/*
    Lights blocked by walls (0.98.7, the owner, 1 November 2026: "Lights blocked by walls"): every light that lights the
    ground (a flashlight's beam, a street light's pool, a fire's glow, a blast's or a muzzle's flash) is drawn as a
    fan from the light, cut where a wall, a shut door, a crate or a tree's trunk stands in its way (managers/
    lightShadowLogic.ts says the fan's shape from what stands on the light's floor): a doorway throws a beam of light
    across the street at night, a crate stands in its own dark, a lamp lights the street and not the room behind the
    wall. The night's dark (managers/nightLight.ts) and the day's glows (managers/lighting.ts) draw their lights with
    these fans, with the shadows on (Low and High); Off keeps the round lights of before.

    With the lighting at High the fans also draw the lights' relief (`relief`): each light's own picture of how it
    strikes the shapes on screen (managers/normalLook.ts: a crate's near side lit, its far side shaded), added into a
    small picture the grade reads (war/warShaders.ts). The fans are meshes (a triangle from the light to each pair of
    the fan's points), the lights' pictures stretched over them.

    And a roof shown over a light keeps it off (`RoofMaskedLights`): a fire in a shed, a flashlight in a house, a lamp
    by it light the floor under the roof and what the walls let out through a door or a window, never the roof seen
    from outside (the lights are drawn over the whole world: the roofs are masked out of them, out to their walls).
*/

/** a light's relief round it when it has no fan: 24 points round a circle of one */
const ROUND = (() => {
    const out = new Float32Array(48);
    for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2;
        out[i * 2] = Math.cos(a);
        out[i * 2 + 1] = Math.sin(a);
    }
    return out;
})();

/** a fan's triangles by its count, closed or not, and its buffers' size (they're the same from frame to frame) */
const indexCache = new Map<string, Uint32Array>();
const indicesOf = (count: number, closed: boolean, capacity: number): Uint32Array => {
    const key = `${count}:${closed ? 1 : 0}:${capacity}`;
    let indices = indexCache.get(key);
    if (!indices) {
        indices = fanIndices(count, closed, capacity);
        indexCache.set(key, indices);
        // (a few hundred kept at most: the counts a light's fan has)
        if (indexCache.size > 600) indexCache.delete(indexCache.keys().next().value as string);
    }
    return indices;
};

/**
 * a fan drawn as a mesh with a light's picture over it (blended as its scene wants: erased, added). Drawn on its own,
 * not in a batch (its vertices change each frame: a batch would be built again with them), its buffers a few sizes
 * and written again (0.98.7's audit)
 */
export class FanMesh {
    readonly mesh: Mesh<MeshGeometry>;
    private readonly _geometry: MeshGeometry;
    private _positions = new Float32Array(0);
    private _uvs = new Float32Array(0);

    constructor(texture: Texture, blendMode: "erase" | "add" | "normal") {
        this._geometry = new MeshGeometry({
            positions: new Float32Array([0, 0, 1, 0, 1, 1]),
            uvs: new Float32Array([0, 0, 1, 0, 1, 1]),
            indices: new Uint32Array([0, 1, 2])
        });
        this._geometry.batchMode = "no-batch";
        this.mesh = new Mesh({ geometry: this._geometry, texture });
        this.mesh.blendMode = blendMode;
        this.mesh.eventMode = "none";
    }

    /**
     * the fan: the light at (cx, cy) and the fan's points (`fan`, game units in their order, as `LightFans.fan` gives
     * them: k × a point + (ox, oy) is where it's drawn, in the mesh's pixels), `closed` when they go all round; `uv`
     * maps where each point (in the mesh's pixels) reads the picture
     */
    set(cx: number, cy: number, fan: Float32Array, closed: boolean, uv: UvMap, k: number, ox = 0, oy = 0): void {
        const n = fan.length >> 1;
        const capacity = fanCapacity(n);
        const size = (capacity + 1) * 2;
        // (0.98.8: each buffer sent to the card again only when a value in it changed, compared as it's written: a
        // lamp's fan, and a light's that hasn't moved, are the same frame after frame)
        let movedP = false, movedU = false;
        if (this._positions.length !== size) {
            this._positions = new Float32Array(size);
            this._uvs = new Float32Array(size);
            movedP = movedU = true;
        }
        const positions = this._positions, uvs = this._uvs;
        const cu = uv.u0 + uv.ux * cx + uv.uy * cy, cv = uv.v0 + uv.vx * cx + uv.vy * cy;
        let was = positions[0]; positions[0] = cx; if (positions[0] !== was) movedP = true;
        was = positions[1]; positions[1] = cy; if (positions[1] !== was) movedP = true;
        was = uvs[0]; uvs[0] = cu; if (uvs[0] !== was) movedU = true;
        was = uvs[1]; uvs[1] = cv; if (uvs[1] !== was) movedU = true;
        for (let i = 0; i < n; i++) {
            const x = fan[i * 2] * k + ox, y = fan[i * 2 + 1] * k + oy;
            const p = (i + 1) * 2;
            was = positions[p]; positions[p] = x; if (positions[p] !== was) movedP = true;
            was = positions[p + 1]; positions[p + 1] = y; if (positions[p + 1] !== was) movedP = true;
            was = uvs[p]; uvs[p] = uv.u0 + uv.ux * x + uv.uy * y; if (uvs[p] !== was) movedU = true;
            was = uvs[p + 1]; uvs[p + 1] = uv.v0 + uv.vx * x + uv.vy * y; if (uvs[p + 1] !== was) movedU = true;
        }
        // (the room left over: at the light, read where it reads, the empty triangles' corners)
        for (let i = n + 1; i <= capacity; i++) {
            const p = i * 2;
            was = positions[p]; positions[p] = cx; if (positions[p] !== was) movedP = true;
            was = positions[p + 1]; positions[p + 1] = cy; if (positions[p + 1] !== was) movedP = true;
            was = uvs[p]; uvs[p] = cu; if (uvs[p] !== was) movedU = true;
            was = uvs[p + 1]; uvs[p + 1] = cv; if (uvs[p + 1] !== was) movedU = true;
        }
        if (movedP) this._geometry.positions = positions;
        if (movedU) this._geometry.uvs = uvs;
        const indices = indicesOf(n, closed, capacity);
        if (indices !== this._indices) {
            this._indices = indices;
            this._geometry.indices = indices;
        }
    }

    /** the triangles last given the geometry (0.98.8: given again only when they're others) */
    private _indices?: Uint32Array;

    destroy(): void {
        this.mesh.destroy();
        this._geometry.destroy(true);
    }
}

/** a roof shown now (its building's ceiling drawn, the building's floor shown): the floor it covers and its shape */
interface ShownRoof {
    readonly layer: Layer
    readonly hitbox: Hitbox
}

/** a roof keeps the lights off this far out past its ceiling's hitbox (game units): over its walls to their outer faces */
export const ROOF_EAVES = 1.8;

/** a hitbox drawn into `graphics` (world pixels), each rectangle and circle `grow` units bigger all round */
function drawRoof(graphics: Graphics, hitbox: Hitbox, grow: number): void {
    switch (hitbox.type) {
        case HitboxType.Rect: {
            const { min, max } = hitbox;
            graphics.rect((min.x - grow) * PIXI_SCALE, (min.y - grow) * PIXI_SCALE, (max.x - min.x + grow * 2) * PIXI_SCALE, (max.y - min.y + grow * 2) * PIXI_SCALE);
            break;
        }
        case HitboxType.Circle:
            graphics.circle(hitbox.position.x * PIXI_SCALE, hitbox.position.y * PIXI_SCALE, (hitbox.radius + grow) * PIXI_SCALE);
            break;
        case HitboxType.Polygon:
            graphics.poly(hitbox.points.flatMap(point => [point.x * PIXI_SCALE, point.y * PIXI_SCALE]));
            break;
        case HitboxType.Group:
            for (const part of hitbox.hitboxes) drawRoof(graphics, part, grow);
            break;
    }
}

/**
 * a scene's lights sorted by their floor (0.98.7), each floor's in a container of its own kept off the roofs shown
 * over that floor: a roof on its floor or on one above (a house's over the lights in its rooms, a block's top roof over
 * the yard's lights under it), never one under it (a roof's deck stands over the roofs below, its lights on them). The
 * containers go into `scene`; their masks are drawn in the world's pixels, put into the scene's measure by `update`
 */
export class RoofMaskedLights {
    private readonly _groups = new Map<Layer, { readonly container: Container, readonly mask: Graphics, stamp: string, masked: boolean }>();

    constructor(private readonly _scene: Container) {}

    /** the container a light on `layer` is drawn in */
    group(layer: Layer): Container {
        let group = this._groups.get(layer);
        if (!group) {
            const container = new Container();
            container.eventMode = "none";
            const mask = new Graphics();
            this._scene.addChild(mask, container);
            group = { container, mask, stamp: "", masked: false };
            this._groups.set(layer, group);
        }
        return group.container;
    }

    /**
     * every frame, after `LightFans.update` and before the scene is drawn: each floor's mask the roofs shown over it
     * now (redrawn when they changed), placed by the scene's measure: a world pixel at (`x` + `scale` × it)
     */
    update(x = 0, y = 0, scale = 1): void {
        for (const [layer, group] of this._groups) {
            group.mask.position.set(x, y);
            group.mask.scale.set(scale);
            const stamp = LightFans.roofStamp(layer);
            if (stamp !== group.stamp) {
                group.stamp = stamp;
                group.mask.clear();
                let any = false;
                for (const roof of LightFans.roofsOver(layer)) {
                    drawRoof(group.mask, roof.hitbox, ROOF_EAVES);
                    any = true;
                }
                if (any) group.mask.fill({ color: 0xffffff });
                if (any !== group.masked) {
                    group.masked = any;
                    if (any) group.container.setMask({ mask: group.mask, inverse: true });
                    else group.container.mask = null;
                }
            }
        }
    }
}

/** a light asked for this frame: where (game units), its floor, its reach, its cone, how high it hangs (a lamp's, up its post) */
export interface FanLight {
    readonly key: string
    readonly x: number
    readonly y: number
    readonly layer: Layer
    readonly radius: number
    readonly cone?: Cone
    readonly height?: number
}

/** what blocks a light: anything this tall (game units); a light hung `TALL_LIGHT` or higher, only what's `TALL_BLOCKER` tall */
export const BLOCKER = 1.8;
export const TALL_LIGHT = 3.5;
export const TALL_BLOCKER = 2.8;

interface Cached {
    x: number
    y: number
    radius: number
    cone?: Cone
    stamp: number
    /** the segments it was made among (0.98.8: the same again, the same fan) */
    near: Float32Array
    points: Float32Array
    used: number
}

/** two lists of segments the same, number for number */
const sameSegments = (a: Float32Array, b: Float32Array): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
};

class LightFansClass {
    private readonly _segments = new Map<Layer, { stamp: number, segments: Float32Array }>();
    /** the roofs shown now (0.98.7: the lights are kept off them), and their stamp, which changes when they do */
    private _roofs: ShownRoof[] = [];
    private _roofsStamp = "";
    private readonly _cache = new Map<string, Cached>();
    private _frame = 0;
    private _shaped = 0;
    /** the fans shaped this frame (tests and the lab) */
    get shaped(): number { return this._shaped; }
    /** the relief's lights drawn this frame (the lab) */
    private _reliefDrawn = 0;
    get reliefDrawn(): number { return this._reliefDrawn; }

    private _reliefTarget?: RenderTexture;
    private readonly _reliefScene = new Container();
    /** the relief's lights by their floor, off the roofs shown over them */
    private readonly _reliefLights = new RoofMaskedLights(this._reliefScene);
    private readonly _reliefPool: Array<{ mesh: Mesh<MeshGeometry, Shader>, geometry: MeshGeometry, uniforms: Record<string, unknown>, positions: Float32Array, indices?: Uint32Array }> = [];
    private _reliefUsed = 0;
    private _reliefBefore = 0;
    private _reliefOn = false;

    /** the fans are shaped: the shadows on (Low or High), a match on, a renderer that draws meshes into a texture */
    get active(): boolean {
        return GameConsole.getBuiltInCVar("cv_shadows") !== "off" && Game.gameStarted && Game.pixi.renderer.type !== RendererType.CANVAS;
    }

    /** the relief's picture this frame, or none */
    get relief(): Texture | undefined { return this._reliefOn ? this._reliefTarget : undefined; }

    /**
     * every frame, after the normals' picture and before the lights are drawn (the day's glows, the night's lights):
     * the walls gathered afresh, the frame counted, the relief's picture begun
     */
    update(): void {
        this._frame++;
        this._shaped = 0;
        this._segments.clear();
        // (a fan not asked for in a while is forgotten)
        if (this._frame % 120 === 0) {
            for (const [key, cached] of this._cache) if (this._frame - cached.used > 300) this._cache.delete(key);
        }
        // (0.98.8: the relief's lights drawn last frame and not this one hidden at its end, the rest left shown)
        this._reliefBefore = this._reliefUsed;
        this._reliefUsed = 0;
        this._reliefDrawn = 0;
        this._reliefOn = false;
        this._gatherRoofs();
        this._reliefReady = this._beginRelief();
    }

    /**
     * the roofs shown now: every building's ceiling drawn (as good as: half faded in at least) on a floor shown, none
     * torn off by a tornado, none a roof's deck (a floor). None while the fans are off (Off: the round lights of before)
     */
    private _gatherRoofs(): void {
        const roofs: ShownRoof[] = [];
        let stamp = "";
        if (this.active) {
            for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
                const hitbox = building.ceilingHitbox;
                if (!hitbox || building.destroyed || building.definition.openAir || building.roof === RoofState.Torn) continue;
                if (building.dead && !building.definition.hasDamagedCeiling) continue;
                // (its ceiling shown as the building says, and as good as drawn: one a room keeps half seen from inside
                // it, `ceilingHiddenAlpha`, is hidden)
                const floor = building.layerContainer;
                const ceiling = building.ceilingContainer;
                if (!building.ceilingVisible || !floor?.visible || floor.alpha < 0.5 || !ceiling.visible || ceiling.alpha < 0.5) continue;
                roofs.push({ layer: building.layer, hitbox });
                stamp += `${building.id}:${building.layer};`;
            }
        }
        if (stamp !== this._roofsStamp) {
            this._roofsStamp = stamp;
            this._roofs = roofs;
        }
    }

    /**
     * how far along `angle` from (x, y) (game units, a shooter's middle) a muzzle's flash stands on `layer`, `length`
     * at most: a little short of the first wall in the way, so it lights the shooter's side (0.98.7's audit)
     */
    muzzleReach(x: number, y: number, angle: number, length: number, layer: Layer): number {
        if (!this.active) return length;
        const walls = this._walls(layer, false);
        return Math.max(0, rayReach(x, y, Math.cos(angle), Math.sin(angle), walls.segments, length + 0.6) - 0.6);
    }

    /** the roofs shown over a light on `layer`: those on its floor or above */
    roofsOver(layer: Layer): ShownRoof[] {
        return this._roofs.filter(roof => roof.layer >= layer);
    }

    /** a stamp of the roofs shown over a light on `layer`, which changes when they do */
    roofStamp(layer: Layer): string {
        return `${layer}|${this._roofsStamp}`;
    }

    /** after the last light is drawn: the relief's picture drawn for the grade */
    finish(): void {
        this._endRelief();
    }

    /** leaving a match */
    reset(): void {
        this._cache.clear();
        this._segments.clear();
        this._reliefOn = false;
        this._roofs = [];
        this._roofsStamp = "";
    }

    /**
     * the walls on `layer` this frame (game units), with a stamp that changes when they do: `tall`, only what stands
     * as tall as a wall (a lamp high on its post is blocked by the walls and the trees, not by a crate under it)
     */
    private _walls(layer: Layer, tall: boolean): { stamp: number, segments: Float32Array } {
        const key = (layer * 2 + (tall ? 1 : 0)) as Layer;
        const known = this._segments.get(key);
        if (known) return known;
        const least = tall ? TALL_BLOCKER : BLOCKER;
        const out: number[] = [];
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const { width, height } = Game.pixi.screen;
        const margin = 100;
        const minX = -offset.x / scale / PIXI_SCALE - margin, minY = -offset.y / scale / PIXI_SCALE - margin;
        const maxX = minX + width / scale / PIXI_SCALE + margin * 2, maxY = minY + height / scale / PIXI_SCALE + margin * 2;
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            // (on the floors that stand in a light's way on its own: the game's rule for what's in a player's way, so a
            // flight of stairs has both its floors' walls round it: 0.98.7's audit)
            if (obstacle.destroyed || obstacle.dead || !adjacentOrEquivLayer(obstacle, layer)) continue;
            const { x, y } = obstacle.position;
            if (x < minX || x > maxX || y < minY || y > maxY) continue;
            // (a shut door blocks the light; an open one swung aside lets it through its doorway; glass and what's
            // walked through never block it: `blocksLight`)
            if (!blocksLight(obstacle.definition, least, obstacle.door?.offset === 0)) continue;
            if (!obstacle.hitbox) continue;
            segmentsOf(obstacle.hitbox, out);
        }
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            if (building.destroyed || building.dead || !adjacentOrEquivLayer(building, layer) || !building.hitbox) continue;
            const { x, y } = building.position;
            if (x < minX - 200 || x > maxX + 200 || y < minY - 200 || y > maxY + 200) continue;
            segmentsOf(building.hitbox, out);
        }
        const segments = new Float32Array(out);
        let stamp = segments.length;
        for (let i = 0; i < segments.length; i++) stamp = (stamp + segments[i] * (1 + (i % 7))) % 1e9;
        const result = { stamp, segments };
        this._segments.set(key, result);
        return result;
    }

    /**
     * a light's fan this frame (world units, the points in their order; all round unless `cone`), kept from the
     * last frame when the light stood still and nothing round it changed; or none when the fans are off or too many
     * are shaped already
     */
    fan(light: FanLight): Float32Array | undefined {
        if (!this.active) return undefined;
        const walls = this._walls(light.layer, (light.height ?? 0) >= TALL_LIGHT);
        const cached = this._cache.get(light.key);
        const sameCone = (a?: Cone, b?: Cone): boolean => (!a && !b) || (!!a && !!b && Math.abs(a.angle - b.angle) < 1e-4 && Math.abs(a.half - b.half) < 1e-4);
        if (cached && cached.stamp === walls.stamp && Math.abs(cached.x - light.x) < STILL && Math.abs(cached.y - light.y) < STILL && Math.abs(cached.radius - light.radius) < 1e-3 && sameCone(cached.cone, light.cone)) {
            cached.used = this._frame;
            return cached.points;
        }
        // (0.98.8: the walls in its reach the same as when its fan was made, and the light where it was, reaching as
        // far, turned the same way: the same fan, whatever changed further off. As the camera moved, the walls coming
        // into the view's margin had every light's fan made again every frame)
        const near = nearSegments(light.x, light.y, light.radius, walls.segments);
        if (cached && cached.x === light.x && cached.y === light.y && cached.radius === light.radius && sameCone(cached.cone, light.cone) && (cached.cone?.angle ?? 0) === (light.cone?.angle ?? 0) && (cached.cone?.half ?? 0) === (light.cone?.half ?? 0) && sameSegments(cached.near, near)) {
            cached.stamp = walls.stamp;
            cached.used = this._frame;
            return cached.points;
        }
        // (past the frame's budget: the last fan of a light that stood still as it was, reaching as far and turned the
        // same way, only the walls round it changed; a light that moved or turned drawn round, its old fan another
        // shape where it is now, 0.98.7's audit)
        if (this._shaped >= MOST_FANS) {
            const same = cached && Math.abs(cached.x - light.x) < STILL && Math.abs(cached.y - light.y) < STILL && Math.abs(cached.radius - light.radius) < 1e-3 && sameCone(cached.cone, light.cone);
            return same ? cached.points : undefined;
        }
        this._shaped++;
        const points = fanAmong(light.x, light.y, light.radius, near, light.cone);
        this._cache.set(light.key, { x: light.x, y: light.y, radius: light.radius, cone: light.cone, stamp: walls.stamp, near, points, used: this._frame });
        return points;
    }

    private _reliefReady = false;

    /** the relief's picture made ready for the lights to add theirs */
    private _beginRelief(): boolean {
        if (!NormalLook.texture || !Lighting.rich || Game.pixi.renderer.type !== RendererType.WEBGL) return false;
        const screen = Game.pixi.screen;
        const width = Math.max(1, Math.ceil(screen.width * RELIEF_SCALE)), height = Math.max(1, Math.ceil(screen.height * RELIEF_SCALE));
        if (!this._reliefTarget) {
            this._reliefTarget = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
        } else if (this._reliefTarget.width !== width || this._reliefTarget.height !== height) {
            this._reliefTarget.resize(width, height);
        }
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        this._reliefScene.scale.set(scale * RELIEF_SCALE, scale * RELIEF_SCALE);
        this._reliefScene.position.set(offset.x * RELIEF_SCALE, offset.y * RELIEF_SCALE);
        return true;
    }

    /**
     * a light's relief added to the picture: the light at (x, y) in game units on `layer`, reaching `radius` units,
     * `height` units up, `strength` how boldly it models the shapes, `cone` a beam's
     */
    addRelief(x: number, y: number, layer: Layer, radius: number, height: number, strength: number, cone: Cone | undefined, points: Float32Array | undefined, closed: boolean): void {
        const normal = NormalLook.texture;
        if (!normal || !this._reliefReady || strength <= 0) return;
        let entry = this._reliefPool[this._reliefUsed];
        if (!entry) {
            const geometry = new MeshGeometry({ positions: new Float32Array([0, 0, 1, 0, 1, 1]), uvs: new Float32Array([0, 0, 1, 0, 1, 1]), indices: new Uint32Array([0, 1, 2]) });
            geometry.batchMode = "no-batch";
            const shader = new Shader({
                glProgram: GlProgram.from({ vertex: RELIEF_VERTEX, fragment: RELIEF_FRAGMENT, name: "light-relief" }),
                resources: {
                    uNormal: normal.source,
                    reliefUniforms: {
                        uCamera: { value: new Float32Array([0, 0, 1]), type: "vec3<f32>" },
                        uScreen: { value: new Float32Array([1, 1]), type: "vec2<f32>" },
                        uLight: { value: new Float32Array([0, 0]), type: "vec2<f32>" },
                        uRadius: { value: 1, type: "f32" },
                        uHeight: { value: 1, type: "f32" },
                        uStrength: { value: 1, type: "f32" },
                        uCone: { value: new Float32Array([0, 0, 0]), type: "vec3<f32>" }
                    }
                }
            });
            const mesh = new Mesh({ geometry, shader });
            mesh.blendMode = "add";
            mesh.eventMode = "none";
            entry = { mesh, geometry, uniforms: shader.resources.reliefUniforms.uniforms as Record<string, unknown>, positions: new Float32Array(0) };
            this._reliefPool.push(entry);
        }
        this._reliefUsed++;
        const group = this._reliefLights.group(layer);
        if (entry.mesh.parent !== group) group.addChild(entry.mesh);
        entry.mesh.visible = true;
        const shader = entry.mesh.shader;
        if (shader) shader.resources.uNormal = normal.source;
        const u = entry.uniforms;
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const camera = u.uCamera as Float32Array;
        camera[0] = offset.x; camera[1] = offset.y; camera[2] = scale;
        const screen = u.uScreen as Float32Array;
        screen[0] = Game.pixi.screen.width; screen[1] = Game.pixi.screen.height;
        const light = u.uLight as Float32Array;
        light[0] = x * PIXI_SCALE; light[1] = y * PIXI_SCALE;
        u.uRadius = radius * PIXI_SCALE;
        u.uHeight = height * PIXI_SCALE;
        u.uStrength = strength;
        const c = u.uCone as Float32Array;
        c[0] = cone?.angle ?? 0; c[1] = 0; c[2] = cone?.half ?? 0;
        // the fan's shape, or a round of the light's reach when it has none (a circle kept once: `ROUND`)
        const px = x * PIXI_SCALE, py = y * PIXI_SCALE;
        const round = points ? closed : true;
        const count = points ? points.length >> 1 : ROUND.length >> 1;
        const capacity = fanCapacity(count);
        const size = (capacity + 1) * 2;
        // (0.98.8: the buffers sent to the card again only when a value in them changed, compared as it's written)
        let moved = false;
        if (entry.positions.length !== size) {
            entry.positions = new Float32Array(size);
            entry.geometry.uvs = new Float32Array(size);
            moved = true;
        }
        const positions = entry.positions;
        let was = positions[0]; positions[0] = px; if (positions[0] !== was) moved = true;
        was = positions[1]; positions[1] = py; if (positions[1] !== was) moved = true;
        if (points) {
            for (let i = 0; i < count; i++) {
                const p = (i + 1) * 2;
                was = positions[p]; positions[p] = points[i * 2] * PIXI_SCALE; if (positions[p] !== was) moved = true;
                was = positions[p + 1]; positions[p + 1] = points[i * 2 + 1] * PIXI_SCALE; if (positions[p + 1] !== was) moved = true;
            }
        } else {
            const r = radius * PIXI_SCALE;
            for (let i = 0; i < count; i++) {
                const p = (i + 1) * 2;
                was = positions[p]; positions[p] = px + ROUND[i * 2] * r; if (positions[p] !== was) moved = true;
                was = positions[p + 1]; positions[p + 1] = py + ROUND[i * 2 + 1] * r; if (positions[p + 1] !== was) moved = true;
            }
        }
        for (let i = count + 1; i <= capacity; i++) {
            const p = i * 2;
            was = positions[p]; positions[p] = px; if (positions[p] !== was) moved = true;
            was = positions[p + 1]; positions[p + 1] = py; if (positions[p + 1] !== was) moved = true;
        }
        if (moved) entry.geometry.positions = positions;
        const indices = indicesOf(count, round, capacity);
        if (indices !== entry.indices) {
            entry.indices = indices;
            entry.geometry.indices = indices;
        }
        this._reliefDrawn++;
    }

    private _endRelief(): void {
        for (let i = this._reliefUsed; i < this._reliefBefore; i++) this._reliefPool[i].mesh.visible = false;
        this._reliefBefore = this._reliefUsed;
        if (!this._reliefTarget || this._reliefUsed === 0) {
            this._reliefOn = false;
            return;
        }
        // (the scene's children are in the world's pixels: the scene itself takes them to the picture's)
        this._reliefLights.update();
        Game.pixi.renderer.render({ container: this._reliefScene, target: this._reliefTarget, clear: true, clearColor: [0, 0, 0, 1] });
        this._reliefOn = true;
    }
}

export const LightFans = new LightFansClass();
